import React, { useEffect, createElement } from 'react';
import { Helmet } from 'react-helmet-async';
import { AppHeader } from '../components/AppHeader';
import { AppFooter } from '../components/AppFooter';
import { useScreenInit } from '../useScreenInit';
import { MapPin, Shield, Clock, Award, Phone } from 'lucide-react';
const CH_SCRIPT_SRC = 'https://app.companyhub.com/scripts/companyhub.forms.js';
const CH_FORM_URL = 'https://app.companyhub.com/webtolead/renderform/MjIzNzM1?name=Contact_FormMedicalTourism';
export function Contact() {
  useScreenInit('Contact');
  useEffect(() => {
    let cancelled = false;
    let rendered = false;
    let attempts = 0;
    const tryRender = () => {
      if (cancelled || rendered) return true;
      const target = document.getElementById('companyhub-form');
      // @ts-ignore
      const api = window.companyhubForms;
      if (api && typeof api.render === 'function' && target) {
        // @ts-ignore
        api.render(CH_FORM_URL, 'companyhub-form');
        rendered = true;
        return true;
      }
      return false;
    };
    // Ensure the script is present
    if (!document.querySelector(`script[src="${CH_SCRIPT_SRC}"]`)) {
      const script = document.createElement('script');
      script.src = CH_SCRIPT_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
    // Poll for the API to become available, then render once.
    if (!tryRender()) {
      const interval = setInterval(() => {
        attempts += 1;
        if (tryRender() || attempts > 40) {
          clearInterval(interval);
        }
      }, 300);
      return () => {
        cancelled = true;
        clearInterval(interval);
      };
    }
    return () => {
      cancelled = true;
    };
  }, []);
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-198">
      <Helmet data-id="element-199">
        <title data-id="element-200">Contact a Care Coordinator | Affordable Medical Tourism</title>
        <meta name="description" content="Request a free, no-obligation quote for your medical procedure abroad. Our care coordinators at Kosansh Solutions Inc in Fort Worth, TX are ready to assist you. Call 857-567-2674." data-id="element-201" />
        <style data-id="element-202">{`
          #companyhub-form { width: 100%; min-height: 1000px; }
          #companyhub-form iframe { width: 100% !important; min-height: 1000px !important; border: 0 !important; display: block; }
        `}</style>
      </Helmet>

      <AppHeader data-id="element-203" />

      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20" data-id="element-204">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20" data-id="element-205">
          {/* Left Column: Info & Assurance */}
          <div data-id="element-206">
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl mb-6" data-id="element-207">
              Contact a Care Coordinator
            </h1>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-208">
              Take the first step toward your medical journey. Fill out the
              form, and a dedicated care coordinator from Kosansh Solutions Inc.
              will reach out to discuss your needs, provide illustrative
              pricing, and answer any questions.
            </p>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200 mb-8" data-id="element-209">
              <h2 className="text-xl font-semibold text-slate-900 mb-6" data-id="element-210">
                Why choose us?
              </h2>
              <ul className="space-y-6" data-id="element-211">
                <li className="flex gap-4" data-id="element-212">
                  <div className="flex-shrink-0 mt-1" data-id="element-213">
                    <Shield className="h-6 w-6 text-blue-600" data-id="element-214" />
                  </div>
                  <div data-id="element-215">
                    <h3 className="font-medium text-slate-900" data-id="element-216">
                      Vetted Facilities
                    </h3>
                    <p className="text-slate-600 text-sm mt-1" data-id="element-217">
                      We partner exclusively with internationally accredited
                      hospitals and clinics.
                    </p>
                  </div>
                </li>
                <li className="flex gap-4" data-id="element-218">
                  <div className="flex-shrink-0 mt-1" data-id="element-219">
                    <Award className="h-6 w-6 text-blue-600" data-id="element-220" />
                  </div>
                  <div data-id="element-221">
                    <h3 className="font-medium text-slate-900" data-id="element-222">
                      Board-Certified Specialists
                    </h3>
                    <p className="text-slate-600 text-sm mt-1" data-id="element-223">
                      Access highly credentialed surgeons with proven track
                      records.
                    </p>
                  </div>
                </li>
                <li className="flex gap-4" data-id="element-224">
                  <div className="flex-shrink-0 mt-1" data-id="element-225">
                    <Clock className="h-6 w-6 text-blue-600" data-id="element-226" />
                  </div>
                  <div data-id="element-227">
                    <h3 className="font-medium text-slate-900" data-id="element-228">
                      End-to-End Support
                    </h3>
                    <p className="text-slate-600 text-sm mt-1" data-id="element-229">
                      From initial consultation to post-operative care, we guide
                      you every step of the way.
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="space-y-3" data-id="element-230">
              <div className="flex items-center gap-3 text-slate-700" data-id="element-231">
                <MapPin className="h-5 w-5 text-blue-600 flex-shrink-0" data-id="element-232" />
                <span data-id="element-233">Kosansh Solutions Inc., Fort Worth, TX</span>
              </div>
              <div className="flex items-center gap-3 text-slate-700" data-id="element-234">
                <Phone className="h-5 w-5 text-blue-600 flex-shrink-0" data-id="element-235" />
                <a href="tel:+18575672674" className="font-semibold text-slate-900 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" data-id="element-236">
                  857-567-2674
                </a>
              </div>
            </div>
            <p className="text-sm text-slate-500 mt-4" data-id="element-237">
              For security and privacy, all written communications are handled
              via this secure form. We do not publish direct email addresses.
            </p>
          </div>

          {/* Right Column: Form Embed */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden flex flex-col relative z-10" data-id="element-238">
            <div className="p-8 bg-blue-600 text-white" data-id="element-239">
              <h2 className="text-2xl font-semibold" data-id="element-240">Request a Free Quote</h2>
              <p className="mt-2 text-blue-100" data-id="element-241">
                Your information is secure and confidential.
              </p>
            </div>
            <div className="p-4 sm:p-8 w-full" data-id="element-242">
              {/* CompanyHub Form Container */}
              <div id="companyhub-form" aria-label="Contact request form" style={{
              width: '100%',
              minHeight: '1000px'
            }} data-id="element-243"></div>
            </div>
          </div>
        </div>
      </main>

      <AppFooter data-id="element-244" />
    </div>;
}